import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import { handleUserResponse } from 'src/features/auth/lib';
import { USER_QUERY_KEY } from 'src/features/auth/utils';
import axios from 'src/lib/axios';
import { queryClient } from 'src/lib/react-query';
import { formatResourcePath } from 'src/utils/regex';

import type { LoginResponse, User } from 'src/features/auth/types';
import type { MutationConfig } from 'src/lib/react-query';

const RESOURCE = '/login/';

const formSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
type FormSchemaValues = z.infer<typeof formSchema>;

interface DTO extends FormSchemaValues {}

type MutationFnType = (data: DTO) => Promise<User>;
const mutationFn = (data: DTO): Promise<LoginResponse> => {
    return axios.post(formatResourcePath(RESOURCE), data);
};

// Attempts to log in the user with the provided email and password and sets the token in the local storage
interface UseMutationOptions extends MutationConfig<MutationFnType> {}
const useMutationHook = (options: UseMutationOptions = {}) =>
    useMutation({
        mutationFn: async (data: DTO) => {
            const response = await mutationFn(data);
            const user = handleUserResponse(response);
            return user;
        },
        ...options,
        onSuccess: (user, ...rest) => {
            queryClient.setQueryData([USER_QUERY_KEY], user);
            options?.onSuccess?.(user, ...rest);
        },
    });

export {
    RESOURCE as LOGIN_RESOURCE,
    formSchema as loginFormSchema,
    mutationFn as loginWithEmailAndPassword,
    useMutationHook as useLogin,
};
export type { DTO as LoginDTO, FormSchemaValues as LoginPayload };
